import { useChannel, usePresence, usePresenceListener } from "ably/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { api } from "../../../lib/api";
import styles from "./ProviderChatContent.module.css";
import { ProviderChatInput } from "./ProviderChatInput";
import { ProviderMessageList } from "./ProviderMessageList";

interface Message {
	id: string;
	content: string;
	senderName: string;
	senderType: "patient" | "provider";
	senderId: string;
	createdAt: Date;
	status?: "sent" | "delivered" | "read";
}

interface ProviderChatContentProps {
	chatRoomId: string;
	participantName: string;
	currentUserId: string;
	currentUserName: string;
	userType: "patient" | "provider";
}

export function ProviderChatContent({
	chatRoomId,
	participantName,
	currentUserId,
	currentUserName,
	userType,
}: ProviderChatContentProps) {
	const { logCommunication } = useAnalytics();
	const hasTrackedConnection = useRef(false);
	const [messages, setMessages] = useState<Message[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [hasMore, setHasMore] = useState(false);
	const [offset, setOffset] = useState(0);
	const [typingUsers, setTypingUsers] = useState<
		{ userId: string; username: string }[]
	>([]);

	// Use Ably hooks for real-time messaging
	const { publish } = useChannel(`chat:${chatRoomId}`, (message) => {
		if (
			message.name === "new-message" &&
			message.data.senderId !== currentUserId
		) {
			const newMessage: Message = {
				...message.data,
				createdAt: new Date(message.data.createdAt),
			};
			setMessages((prev) => [...prev, newMessage]);
			const messageContent = message.data.content || "";
			// HIPAA: provider-chat messages are medical free-text — send only the
			// length, never the content (the previous message_preview slice was a
			// PHI leak and has been removed).
			logCommunication("provider_message_received", {
				chat_room_id: chatRoomId,
				sender_type: message.data.senderType,
				user_type: userType,
				message_length: messageContent.length,
			});
		} else if (message.name === "typing") {
			const { userId, isTyping, username } = message.data;
			if (userId !== currentUserId) {
				setTypingUsers((prev) => {
					if (isTyping) {
						const existingUser = prev.find((user) => user.userId === userId);
						return existingUser ? prev : [...prev, { userId, username }];
					}
					return prev.filter((user) => user.userId !== userId);
				});
			}
		}
	});

	// Connection status
	const [isConnected, _setIsConnected] = useState(true);

	// Use Ably presence to track online users
	const { updateStatus } = usePresence(`chat:${chatRoomId}`, {
		userId: currentUserId,
		userType: userType,
		username: currentUserName,
	});

	// Listen to presence updates
	const { presenceData } = usePresenceListener(`chat:${chatRoomId}`);

	// Check if the other participant is online
	const isParticipantOnline = presenceData.some(
		(member: { clientId?: string }) => member.clientId !== currentUserId,
	);

	// Track provider online/offline status changes
	const prevOnlineStatus = useRef<boolean | null>(null);
	useEffect(() => {
		// Only track after initial load (skip first render)
		if (
			prevOnlineStatus.current !== null &&
			prevOnlineStatus.current !== isParticipantOnline
		) {
			logCommunication("provider_participant_status_changed", {
				chat_room_id: chatRoomId,
				user_type: userType,
				participant_status: isParticipantOnline ? "online" : "offline",
			});
		}
		prevOnlineStatus.current = isParticipantOnline;
	}, [isParticipantOnline, chatRoomId, userType, logCommunication]);

	// Load chat history
	const loadMessages = useCallback(
		async (loadOffset = 0, append = false) => {
			try {
				setIsLoading(true);
				const response = await api.get<{
					messages: Message[];
					hasMore: boolean;
					nextOffset: number;
				}>(
					`/api/provider-chat/history/${chatRoomId}?limit=20&offset=${loadOffset}`,
				);

				if (response.data) {
					const formattedMessages = response.data.messages.map((msg) => ({
						...msg,
						createdAt: new Date(msg.createdAt),
					}));

					if (append) {
						setMessages((prev) => [...formattedMessages, ...prev]);
					} else {
						setMessages(formattedMessages);
						// Track initial connection/load
						if (!hasTrackedConnection.current) {
							hasTrackedConnection.current = true;
							logCommunication("provider_connected", {
								chat_room_id: chatRoomId,
								user_type: userType,
								initial_message_count: formattedMessages.length,
							});
						}
					}

					setHasMore(response.data.hasMore);
					setOffset(response.data.nextOffset);
				}
			} catch (error) {
				console.error("Error loading messages:", error);
			} finally {
				setIsLoading(false);
			}
		},
		[chatRoomId, logCommunication, userType],
	);

	// Load more messages (for pagination)
	const loadMoreMessages = useCallback(() => {
		if (!isLoading && hasMore) {
			loadMessages(offset, true);
		}
	}, [loadMessages, offset, isLoading, hasMore]);

	// Send message
	const sendMessage = useCallback(
		async (content: string) => {
			try {
				// First save to database
				const response = await api.post<{ message: Message }>(
					`/api/provider-chat/history/${chatRoomId}`,
					{ content },
				);

				if (response.data) {
					const newMessage = {
						...response.data.message,
						createdAt: new Date(response.data.message.createdAt),
					};

					// Add message to local state for immediate display
					setMessages((prev) => [...prev, newMessage]);

					// Publish to Ably for real-time delivery
					await publish("new-message", newMessage);

					// HIPAA: send only the length, never the content (removed the
					// previous message_preview slice — a PHI leak).
					logCommunication("provider_message_sent", {
						chat_room_id: chatRoomId,
						user_type: userType,
						message_length: content.length,
					});
				}
			} catch (error) {
				console.error("Error sending message:", error);
			}
		},
		[chatRoomId, publish, logCommunication, userType],
	);

	// Handle typing indicators
	const handleTyping = useCallback(
		async (isTyping: boolean) => {
			try {
				// Use the current user's name for typing indicator
				await publish("typing", {
					userId: currentUserId,
					username: currentUserName,
					isTyping,
					timestamp: Date.now(),
				});
			} catch (error) {
				console.error("Error sending typing indicator:", error);
			}
		},
		[currentUserId, currentUserName, publish],
	);

	// Load initial messages
	useEffect(() => {
		loadMessages();
	}, [loadMessages]);

	// Update presence status when component mounts/unmounts
	useEffect(() => {
		if (updateStatus) {
			updateStatus({
				userId: currentUserId,
				userType: userType,
				username: currentUserName,
			});
		}

		// Cleanup: Leave presence when component unmounts
		return () => {
			// Presence automatically handles cleanup when component unmounts
		};
	}, [updateStatus, currentUserId, userType, currentUserName]);

	return (
		<div className={styles.container}>
			{/* Chat header - only show in B2B context */}
			{userType === "provider" && (
				<div className={styles.header}>
					<div className={styles.headerContent}>
						<div>
							<h3 className={styles.headerTitle}>{participantName}</h3>
							<div className={styles.statusContainer}>
								<div
									className={
										isParticipantOnline
											? styles.statusOnline
											: styles.statusOffline
									}
								/>
								<span className={styles.statusText}>
									{isParticipantOnline ? "Online" : "Offline"}
								</span>
								{!isConnected && (
									<span className={styles.statusConnecting}>
										• Connecting...
									</span>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Messages */}
			<ProviderMessageList
				messages={messages}
				currentUserId={currentUserId}
				isLoading={isLoading}
				onLoadMore={loadMoreMessages}
				hasMore={hasMore}
				typingUsers={typingUsers.map((user) => user.username)}
			/>

			{/* Chat input */}
			<ProviderChatInput
				onSendMessage={sendMessage}
				onTyping={handleTyping}
				disabled={!isConnected}
				placeholder={`Message ${participantName}...`}
			/>
		</div>
	);
}
